import React, { type FunctionComponent, useCallback } from 'react'
import {
  RouterTabs,
  RouterTab,
  RouterTabList,
  RouterTabPanel,
  RouterTabPanels,
  colors,
  spacing,
  borderRadius,
} from '@retailer-platform/shared-components'
import styled from '@emotion/styled'
import { StringParam, useQueryParam } from 'use-query-params'
import { IssueIcon } from '@instacart/ids-core'
import { ProductsViewAttributeOverrides } from 'domains/products/components/view/components/attribute-overrides/ProductsViewAttributeOverrides'
import { ProductsViewAisles } from 'domains/products/components/view/components/aisles/ProductsViewAisles'
import { useGetProduct } from 'domains/products/components/view/utils/useGetProduct.hooks'
import { type InstacartCustomersSharedV1Locale } from 'domains/products/api'
import { useWarehouseContext } from 'utils/contexts/warehouse/WarehouseContext.hooks'
import { ProductsViewDetails } from 'domains/products/components/view/components/details/ProductsViewDetails'
import { useDomainMessages } from 'domains/products/utils/domain/intl'
import { LoadingPage } from 'gin-and-tonic/containers/loading-page/LoadingPage'
import { routes } from 'domains/products/routing/routes'
import { ErrorPage } from 'gin-and-tonic/containers/error-page/ErrorPage'
import { ProductsViewStoreLocations } from 'domains/products/components/view/components/store-locations/ProductsViewStoreLocations'
import { ProductTranslationNotFoundError } from 'domains/products/components/view/utils/errors'
import { ProductsViewNotFoundErrorPage } from 'domains/products/components/view/components/not-found-error-page/ProductsViewNotFoundErrorPage'
import { type OnLocaleChangeCallback } from 'gin-and-tonic/containers/locale-select/LocaleSelect'
import { ProductsViewHeader } from 'domains/products/components/view/components/header/ProductsViewHeader'
import { useDomainAccessControl } from 'domains/products/utils/domain/accessControl'
import {
  productsViewVBOAccessControl,
  productsViewInAssortmentAccessControl,
} from 'domains/products/access-control/viewAccess.configuration'
interface ProductsViewProps {
  productId: string
  retailerId: string
  partnerId: string
}

const LimitedHeightContainer = styled.div({
  height: 'calc(100vh - 86px)',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
})

const tabs = {
  details: routes['products-view-details'],
  storeLocations: routes['products-view-store-locations'],
  aisles: routes['products-view-aisles'],
  attributeOverrides: routes['products-view-attribute-overrides'],
}

export const ProductsView: FunctionComponent<ProductsViewProps> = ({
  productId,
  retailerId,
  partnerId,
}) => {
  const { warehouse } = useWarehouseContext()
  const hasMultipleLocales = warehouse.locales.length > 1

  const [locale = warehouse.defaultLocale.localeCode, setLocaleQueryParam] = useQueryParam(
    'locale',
    StringParam
  )

  const onLocaleChange: OnLocaleChangeCallback = useCallback(
    locale => setLocaleQueryParam(locale.localeCode),
    [setLocaleQueryParam]
  )

  const { product, loading, error, refetch } = useGetProduct({
    productId,
    retailerId,
    localeCode: locale.toUpperCase() as InstacartCustomersSharedV1Locale,
  })

  const messages = useDomainMessages({
    details: 'productsDomain.view.tabs.details',
    storeLocations: 'productsDomain.view.tabs.storeLocations',
    aisles: 'productsDomain.view.tabs.aisles',
    ariaLabel: 'productsDomain.view.tabs.ariaLabel',
    attributeOverrides: 'productsDomain.view.tabs.attributeOverrides',
    delayedWarning: 'productsDomain.view.delayedWarning',
  })

  const hasAccess = useDomainAccessControl()

  const canAccessVBO = hasAccess(productsViewVBOAccessControl)
  const canModifyInAssortment = hasAccess(productsViewInAssortmentAccessControl)

  if (loading) return <LoadingPage />

  if (hasMultipleLocales && error instanceof ProductTranslationNotFoundError) {
    /**
     * If we have multiple locales and a translation failed to load, display a way
     * to change to a locale that is implemented.
     */
    return (
      <ProductsViewNotFoundErrorPage
        onLocaleChange={onLocaleChange}
        locale={locale}
        productId={productId}
      />
    )
  } else if (error) {
    // Otherwise, use generic error screen
    return <ErrorPage action={() => refetch()} />
  }

  return (
    <React.Fragment>
      <ProductsViewHeader product={product} showInAssortment={canModifyInAssortment} />

      <RouterTabs defaultTabId={tabs.details}>
        <RouterTabList aria-label={messages.ariaLabel}>
          <RouterTab path={tabs.details}>{messages.details}</RouterTab>
          <RouterTab path={tabs.storeLocations}>{messages.storeLocations}</RouterTab>
          <RouterTab path={tabs.aisles}>{messages.aisles}</RouterTab>
          {canAccessVBO && (
            <RouterTab path={tabs.attributeOverrides}>{messages.attributeOverrides}</RouterTab>
          )}
        </RouterTabList>

        <RouterTabPanels>
          <RouterTabPanel path={tabs.details}>
            <ProductsViewDetails
              product={product}
              retailerId={retailerId}
              locale={locale}
              showLocaleSelector={hasMultipleLocales}
              onLocaleChange={onLocaleChange}
            />
          </RouterTabPanel>

          <RouterTabPanel path={tabs.storeLocations}>
            <LimitedHeightContainer>
              <ProductsViewStoreLocations
                productId={product.productId}
                warehouseId={retailerId}
                partnerId={partnerId}
                product={null}
              />
            </LimitedHeightContainer>
          </RouterTabPanel>

          <RouterTabPanel path={tabs.aisles}>
            <LimitedHeightContainer>
              <div
                css={{
                  background: colors.LOYALTY.LIGHT,
                  padding: spacing.X12,
                  borderRadius: borderRadius.X8,
                  position: 'absolute',
                  top: 15,
                  right: 110,
                  height: 32,
                  zIndex: 1,
                  fontSize: 13,
                  display: 'flex',
                  flexDirection: 'row',
                }}
              >
                <IssueIcon color="brandLoyaltyRegular" css={{ marginRight: spacing.X8 }} />
                {messages.delayedWarning}
              </div>
              <ProductsViewAisles
                availabilityLoading={false}
                partnerId={partnerId}
                warehouseId={retailerId}
                productId={product.productId}
                product={{
                  // eslint-disable-next-line @typescript-eslint/naming-convention
                  main_aisle: product.aisle,
                  aisles: product.aisles,
                }}
                availableItemsCount={
                  product.availableItemsCount
                    ? parseInt(product.availableItemsCount, 10)
                    : undefined
                }
              />
            </LimitedHeightContainer>
          </RouterTabPanel>

          {canAccessVBO && (
            <RouterTabPanel path={tabs.attributeOverrides}>
              <LimitedHeightContainer>
                <ProductsViewAttributeOverrides
                  productId={product.productId}
                  retailerId={retailerId}
                />
              </LimitedHeightContainer>
            </RouterTabPanel>
          )}
        </RouterTabPanels>
      </RouterTabs>
    </React.Fragment>
  )
}
